<script setup lang="ts">
import 'leaflet/dist/leaflet.css'
import { LMap, LTileLayer } from '@vue-leaflet/vue-leaflet'
import { computed, onMounted, ref, watch } from 'vue'
import { useResizeObserver } from '@vueuse/core'
import MarkerWrapper from './MarkerWrapper.vue'
import { useGiPointStore } from '@/stores/giPoints'

const giPointsStore = useGiPointStore()
const { items: giPoints } = storeToRefs(giPointsStore)

const appHeaderHeight = inject('appHeaderHeight')

const map = ref()
const container = ref<HTMLElement | null>(null)
const RECENTER_MAP_DELAY_MS = 500

const mapHeight = computed(() => {
  return `calc(100vh - ${appHeaderHeight.value}px)`
})

const mapContainerStyle = computed(() => {
  return {
    height: mapHeight.value,
    width: `100%`
  }
})

const debounceInvalidateSize = () => {
  setTimeout(() => {
    map.value?.leafletObject?.invalidateSize(true)
  }, RECENTER_MAP_DELAY_MS)
}

onMounted(() => {
  // recenter the map when the container size changes
  useResizeObserver(container, debounceInvalidateSize)
})

const flyToMarker = (markerCoordinates: number[]) => {
  map.value.leafletObject.flyTo(markerCoordinates, 13)
}

const tileLayerUrl = `https://maps.geoapify.com/v1/tile/osm-carto/{z}/{x}/{y}.png?apiKey=${
  import.meta.env.VITE_APP_GEOAPIFY_API_KEY
}`

watch(giPoints, (value) => {
  if (value.length) {
    const coordinates = [...value[0].geometry.coordinates] as number[]
    flyToMarker([coordinates[1], coordinates[0]])
  }
})
</script>

<template>
  <div ref="container" :style="mapContainerStyle">
    <l-map
      :use-global-leaflet="false"
      ref="map"
      :zoom="4"
      :center="[47.41322, -1.219482]"
      :min-zoom="8"
      :max-zoom="18"
      :options="{ doubleClickZoom: false, preferCanvas: true }"
    >
      <l-tile-layer
        :url="tileLayerUrl"
        attribution="Powered by &copy; Geoapify | &copy; OpenStreetMap contributors"
        layer-type="base"
        name="Geoapify OSM Carto"
      ></l-tile-layer>
      <MarkerWrapper
        @fly-to-marker="flyToMarker"
        v-for="marker in giPoints"
        :item="marker"
        :key="marker.id"
        :map="map"
      />
    </l-map>
  </div>
</template>

<style scoped></style>
